// @owners { team: patients-team }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  Body,
  Button,
  Card,
  Column,
  Description,
  H1,
  Illustration,
  InlineAlert,
  LgPadding,
  NavBarLogo,
  PresentationListItem,
  SmSpacing,
  XsSpacing,
} from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useCallback, useEffect } from 'react';
import menopur from '~shared/assets/images/menopur.png';
import number1 from '~shared/assets/images/number_1.png';
import number2 from '~shared/assets/images/number_2.png';
import number3 from '~shared/assets/images/number_3.png';
import progynyLogo from '~shared/assets/images/progyny_by_alto_logo.png';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { getProgynyWizardFirstFill } from '~shared/selectors/checkout/progyny';
import { useSelectorShared } from '~shared/store';
import { ProgynyPage } from '../../shared/components/ProgynyPage';
import { getProgynyWizardNextRoute } from '../helpers/getProgynyWizardNextRoute';

export const ProgynyWizardIntro = () => {
  const { getCurrentRouteName, goBack, navigate } = useNavigation();
  const { trackPageView } = useAnalytics();
  const isRefill = !useSelectorShared(getProgynyWizardFirstFill);

  const onNext = useCallback(() => {
    const currentRoute = getCurrentRouteName();
    const nextRouteName = getProgynyWizardNextRoute(currentRoute);

    if (!nextRouteName) return;
    navigate(nextRouteName);
  }, [getCurrentRouteName, navigate]);

  useEffect(() => {
    trackPageView({
      event: EVENTS.MEDICATION_MANAGEMENT_OVERVIEW_VIEWED,
      params: {
        is_refill: isRefill,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ProgynyPage
      dismissIcon="chevronleft"
      onDismiss={goBack}
      NavBarCenterContent={<NavBarLogo source={progynyLogo} />}
      headerBackgroundColor={COLORS.BACKGROUND_COLORS.PINK_LIGHTEST}
      HeaderContent={
        <LgPadding>
          <Column center>
            <Illustration
              size="lg"
              source={menopur}
            />
            <XsSpacing />
            <H1
              testID="e2e-progyny-step1-header"
              center
            >
              Let's set up your order in just a few steps
            </H1>
          </Column>
        </LgPadding>
      }
      buttons={[
        <Button
          key="Continue"
          onPress={onNext}
          testID="e2e-progyny-step1-button"
        >
          Continue
        </Button>,
      ]}
    >
      <Card>
        <SmSpacing />
        <PresentationListItem
          LeftContent={
            <Illustration
              size="sm"
              source={number1}
            />
          }
          text={
            <Body>
              <Body fontFamily="semibold">Answer a few questions</Body> about your medications and treatment timing
            </Body>
          }
        />
        <XsSpacing />
        <PresentationListItem
          LeftContent={
            <Illustration
              size="sm"
              source={number2}
            />
          }
          text={
            <Body>
              <Body fontFamily="semibold">We calculate the exact amount</Body> of medication for where you are in your
              fertility cycle
            </Body>
          }
        />
        <XsSpacing />
        <PresentationListItem
          LeftContent={
            <Illustration
              size="sm"
              source={number3}
            />
          }
          text={
            <Body>
              <Body fontFamily="semibold">Select the delivery time</Body> that works best for you
            </Body>
          }
        />
        <LgPadding topPadding={SPACING.STATIC.MD}>
          <InlineAlert>
            <Description>
              These questions will only apply to Gonal-f, Menopur, and Cetrotide. If you have other medications in your
              cart, you'll be able to review your full order later on in checkout.
            </Description>
          </InlineAlert>
        </LgPadding>
      </Card>
    </ProgynyPage>
  );
};
